import {
  Pause as PauseIcon,
  Play as PlayIcon,
  RotateCcw,
  ArrowLeft,
  Maximize,
  Minimize,
  HelpCircle,
} from "lucide-react";

/**
 * GameControlsBar — the bottom action toolbar extracted from
 * GameCanvas.jsx.
 *
 * Five buttons: Pause/Resume · Restart · Quit · Fullscreen ↔ Exit FS ·
 * Legend, plus a desktop-only keyboard-hint footer. Pure prop-driven;
 * no canvas refs, no hooks.
 *
 * Buttons inherit the existing `btn-neon` classes so the look is
 * pixel-identical to pre-extraction.
 */
export default function GameControlsBar({
  paused,
  gameOver,
  won,
  isFullscreen,
  onTogglePause,
  onRestart,
  onExit,
  onToggleFullscreen,
  onToggleLegend,
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-2 sm:gap-3 relative z-20 pointer-events-auto justify-center"
      data-testid="game-controls-bar"
    >
      <button
        type="button"
        onClick={onTogglePause}
        className="btn-neon cyan px-3 py-2 sm:px-4"
        data-testid="pause-btn"
        disabled={gameOver || won}
      >
        <span className="inline-flex items-center gap-1.5 sm:gap-2 pointer-events-none">
          {paused ? <PlayIcon className="w-4 h-4" /> : <PauseIcon className="w-4 h-4" />}
          <span className="hidden xs:inline text-[11px] sm:text-sm">
            {paused ? "Resume" : "Pause"}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={onRestart}
        className="btn-neon px-3 py-2 sm:px-4"
        data-testid="restart-btn"
      >
        <span className="inline-flex items-center gap-1.5 sm:gap-2 pointer-events-none">
          <RotateCcw className="w-4 h-4" />
          <span className="hidden xs:inline text-[11px] sm:text-sm">Restart</span>
        </span>
      </button>
      <button
        type="button"
        onClick={onExit}
        className="btn-neon px-3 py-2 sm:px-4"
        style={{ borderColor: "#ff007f" }}
        data-testid="exit-btn"
      >
        <span className="inline-flex items-center gap-1.5 sm:gap-2 pointer-events-none">
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden xs:inline text-[11px] sm:text-sm">Quit</span>
        </span>
      </button>
      <button
        type="button"
        onClick={onToggleFullscreen}
        className="btn-neon cyan px-3 py-2 sm:px-4"
        data-testid="fullscreen-btn"
        title={isFullscreen ? "Exit fullscreen (Esc)" : "Enter fullscreen"}
      >
        <span className="inline-flex items-center gap-1.5 sm:gap-2 pointer-events-none">
          {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          <span className="hidden xs:inline text-[11px] sm:text-sm">
            {isFullscreen ? "Exit FS" : "Fullscreen"}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={onToggleLegend}
        className="btn-neon px-3 py-2 sm:px-4"
        style={{ borderColor: "#b026ff" }}
        data-testid="legend-btn"
        title="Power-up legend"
      >
        <span className="inline-flex items-center gap-1.5 sm:gap-2 pointer-events-none">
          <HelpCircle className="w-4 h-4" />
          <span className="hidden xs:inline text-[11px] sm:text-sm">Legend</span>
        </span>
      </button>
      <div
        className="ml-3 font-mono-cosmic text-[10px] tracking-widest uppercase text-white/40 hidden md:block"
        data-testid="game-controls-keyboard-hint"
      >
        ← → / A D · Mouse · Touch · Space/Tap launch · P pause
      </div>
    </div>
  );
}
